import { AfterViewInit, Directive, ElementRef, HostListener, Input, Renderer2 } from "@angular/core";
import { Platform } from "@ionic/angular";

@Directive({
  selector: "[appResizeableModal]",
  exportAs: "appResizeableModal",
  standalone: false,
})
export class ResizeableModalDirective implements AfterViewInit {
  @Input("appResizeableModal") modalElementRef: ElementRef | null = null;

  private width: number = 0;
  private minWidth: number = 0;
  private height: number = 0;
  private minHeight: number = 0;
  private resizing: boolean = false;
  private moving: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private positionX = 100;
  private positionY = 100;
  private modalElement: HTMLElement | null = null;
  private backdropElement: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private platform: Platform,
  ) {}

  ngAfterViewInit(): void {
    this.initModalElement();
  }

  initModalElement(): void {
    this.modalElement = this.getModalElement();
    if (this.modalElement && this.platform.width() >= 1320 && this.platform.height() >= 800) {
      const computedStyle = getComputedStyle(this.modalElement);
      this.width = parseFloat(computedStyle.getPropertyValue("--width").trim());
      this.height = parseFloat(computedStyle.getPropertyValue("--height").trim());
      this.minWidth = parseFloat(computedStyle.getPropertyValue("--min-width").trim());
      this.minHeight = parseFloat(computedStyle.getPropertyValue("--min-height").trim());

      this.backdropElement = this.modalElement.shadowRoot?.querySelector("ion-backdrop") as HTMLElement | null;

      const boundingRect = this.modalElement.getBoundingClientRect();
      this.positionX = boundingRect.left;
      this.positionY = boundingRect.top;
      this.updateModalSizeAndPosition();
    }
  }

  startResizing(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  startMoving(event: MouseEvent): void {
    this.moving = true;
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
    event.preventDefault();
  }

  @HostListener("document:mousemove", ["$event"])
  onMouseMove(event: MouseEvent): void {
    const deltaX = event.clientX - this.lastMouseX;
    const deltaY = event.clientY - this.lastMouseY;

    if (this.resizing) {
      this.width += deltaX;
      if (this.width < this.minWidth) {
        this.width = this.minWidth;
      }
      this.height += deltaY;
      if (this.height < this.minHeight) {
        this.height = this.minHeight;
      }
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.updateModalSizeAndPosition();
    } else if (this.moving) {
      this.positionX += deltaX;
      this.positionY += deltaY;

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.updateModalSizeAndPosition();
    }
  }

  @HostListener("document:mouseup")
  onMouseUp(): void {
    this.resizing = false;
    this.moving = false;
  }

  private getModalElement(): HTMLElement | null {
    if (this.modalElementRef) {
      return this.modalElementRef.nativeElement;
    }
    return this.el.nativeElement.closest(".resizeable-modal");
  }

  private updateModalSizeAndPosition(): void {
    if (this.platform.width() < 1320 || this.platform.height() < 800) {
      return;
    }
    if (!this.modalElement) {
      return;
    }
    this.renderer.setStyle(this.modalElement, "--width", `${this.width}px`, 2);
    this.renderer.setStyle(this.modalElement, "--height", `${this.height}px`, 2);
    this.renderer.setStyle(this.modalElement, "transform", `translate(${this.positionX}px, ${this.positionY}px)`);
    if (this.backdropElement) {
      this.renderer.setStyle(this.backdropElement, "transform", `translate(${-this.positionX}px, ${-this.positionY}px)`);
      this.renderer.setStyle(this.backdropElement, "width", "200vw");
      this.renderer.setStyle(this.backdropElement, "height", "200vh");
      this.renderer.setStyle(this.backdropElement, "left", "-50vw");
      this.renderer.setStyle(this.backdropElement, "top", "-50vh");
    }
  }
}
